import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import type { PDFDocumentProxy, RenderTask } from "pdfjs-dist";
import { Text } from "@/components/Typography";
import { loadPdfjs } from "@/utils/pdfjs";

/**
 * A PDF shown one page at a time, the way LinkedIn previews a document post:
 * an "N pages" bar, the page filling the frame's width (a tall page scrolls
 * inside the frame), and round arrows over the left and right edges.
 *
 * pdf.js and its worker load on first use — see utils/pdfjs.
 */

export const PdfCarousel = ({ data }: { data: ArrayBuffer }) => {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [failed, setFailed] = useState(false);
  const [pageIdx, setPageIdx] = useState(0);
  const [width, setWidth] = useState(0);

  const frameRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Load the document. pdf.js TRANSFERS the buffer it is given to its worker,
  // which empties it — so it gets a copy, and the caller's buffer stays whole
  // for the download.
  useEffect(() => {
    let cancelled = false;
    let loaded: PDFDocumentProxy | null = null;
    setDoc(null);
    setFailed(false);
    setPageIdx(0);

    loadPdfjs()
      .then((pdfjs) => pdfjs.getDocument({ data: new Uint8Array(data.slice(0)) }).promise)
      .then((pdf) => {
        loaded = pdf;
        if (cancelled) void pdf.destroy();
        else setDoc(pdf);
      })
      .catch(() => !cancelled && setFailed(true));

    return () => {
      cancelled = true;
      void loaded?.destroy();
    };
  }, [data]);

  // The page is drawn at the frame's width, so redraw when that changes.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Draw the current page, sharp on high-density screens.
  useEffect(() => {
    if (!doc || !width || !canvasRef.current) return;
    let task: RenderTask | null = null;
    let cancelled = false;

    doc
      .getPage(pageIdx + 1)
      .then((page) => {
        if (cancelled || !canvasRef.current) return;
        const canvas = canvasRef.current;
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: width / base.width });
        const ratio = window.devicePixelRatio || 1;

        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;

        const context = canvas.getContext("2d");
        if (!context) return;
        task = page.render({
          canvasContext: context,
          viewport,
          transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined,
        });
        return task.promise;
      })
      .catch((err: unknown) => {
        // A cancelled render is the normal result of paging quickly.
        if ((err as { name?: string })?.name !== "RenderingCancelledException") setFailed(true);
      });

    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [doc, pageIdx, width]);

  // Each page starts at its top, as LinkedIn's viewer does.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [pageIdx]);

  const pageCount = doc?.numPages ?? 0;
  const canPrev = pageIdx > 0;
  const canNext = pageIdx < pageCount - 1;

  useEffect(() => {
    if (!pageCount) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") setPageIdx((i) => Math.max(0, i - 1));
      if (e.key === "ArrowRight") setPageIdx((i) => Math.min(pageCount - 1, i + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pageCount]);

  const arrow =
    "absolute top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/70 text-white shadow-md transition-opacity hover:bg-black/85";

  return (
    <div className="overflow-hidden rounded-xl border border-theme-border">
      <div className="flex items-center justify-between bg-theme-bg-subtle px-4 py-3">
        <Text as="span" className="text-[14px] font-semibold text-theme-text-heading">
          {pageCount ? `${pageCount} ${pageCount === 1 ? "page" : "pages"}` : "Loading pages…"}
        </Text>
        {pageCount > 1 && (
          <Text as="span" className="text-[12px] text-theme-text-muted">
            {pageIdx + 1} / {pageCount}
          </Text>
        )}
      </div>

      <div className="relative bg-theme-bg-subtle px-3 pb-3">
        <div ref={scrollRef} className="max-h-[60vh] overflow-y-auto">
          <div ref={frameRef} className="w-full">
            {failed ? (
              <div className="flex h-[300px] items-center justify-center">
                <Text as="p" className="text-[13px] text-theme-text-muted">
                  Couldn't display the PDF. You can still download it below.
                </Text>
              </div>
            ) : (
              <>
                {!doc && (
                  <div className="flex h-[300px] items-center justify-center text-theme-text-muted">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                )}
                <canvas
                  ref={canvasRef}
                  className={doc ? "block shadow-sm" : "hidden"}
                  aria-label={pageCount ? `Page ${pageIdx + 1} of ${pageCount}` : undefined}
                />
              </>
            )}
          </div>
        </div>

        {!failed && canPrev && (
          <button
            type="button"
            aria-label="Previous page"
            onClick={() => setPageIdx((i) => i - 1)}
            className={`${arrow} left-5`}
          >
            <ChevronLeft size={22} />
          </button>
        )}
        {!failed && canNext && (
          <button
            type="button"
            aria-label="Next page"
            onClick={() => setPageIdx((i) => i + 1)}
            className={`${arrow} right-5`}
          >
            <ChevronRight size={22} />
          </button>
        )}
      </div>
    </div>
  );
};
